import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import LazyCardMedia from 'Components/LazyCardMedia';
import customerImg from '../../Assets/images/customer.png';
import usersImg from '../../Assets/images/users.png';
import projectsImg from '../../Assets/images/projects.png';
import compProjImg from '../../Assets/images/complete-projects.png';
import Typography from '@material-ui/core/Typography';
import BarChartWrapper from '../../Components/Charts/BarChartWrapper';
import GenericTable from '../../Components/GenericTable';
import ChartContainer from '../../Components/Charts/ChartContainer';
import { Dashboard } from '../../Services/DashboardService';
import { useAuthDispatch, useAuthState } from '../../Context';
import { USER_ROLES } from '../../utils/constants';
import { isEmpty } from 'lodash';
import { interactiveCardStyles, pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from '../../theme/commonStyles';
import { useState } from 'react';
import AppLoader from '../../Components/AppLoader';
import { useImpersonation } from '../../hooks/useImpersonation';
import SuperadminInsights from './SuperadminInsights';

const dashboardService = new Dashboard();

const useStyles = makeStyles((theme) => ({
    page: {
        width: '100%',
        padding: 0,
    },
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    root: {
        ...interactiveCardStyles,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 148,
        overflow: 'hidden',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            minHeight: 148,
            alignItems: 'stretch',
        },
    },
    content: {
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(2.25, 2.5),
        },
    },
    cover: {
        width: '100%',
        height: 96,
        margin: 0,
        backgroundColor: 'var(--dm-row-alt, #f5f8fc)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        borderBottom: '1px solid var(--dm-border, #e7ebf0)',
        flexShrink: 0,
        [theme.breakpoints.up('sm')]: {
            width: 120,
            height: 'auto',
            minHeight: 148,
            borderBottom: 0,
            borderRight: '1px solid var(--dm-border, #e7ebf0)',
        },
    },
    cardTitle: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(0.25),
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        lineHeight: 1.35,
        maxWidth: '100%',
        [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
        },
    },
    cardSubTitle: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        fontWeight: 500,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        lineHeight: 1.35,
        maxWidth: '100%',
    },
    cardValue: {
        color: theme.palette.primary.main,
        fontSize: '1.5rem',
        fontWeight: 700,
        marginTop: theme.spacing(1),
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        wordBreak: 'break-word',
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.75rem',
        },
    },
    section: {
        marginTop: theme.spacing(3),
    },
    panel: {
        ...surfaceCardStyles,
        padding: theme.spacing(2, 2.25),
        width: '100%',
        overflow: 'auto',
        boxSizing: 'border-box',
    },
    panelTitle: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.9375rem',
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
        lineHeight: 1.35,
    },
}));

const LegacyDashboard = () => {
    const dispatch = useAuthDispatch();
    const { userDetails } = useAuthState(dispatch);
    const impersonation = useImpersonation();
    const getRole = localStorage.getItem('role');
    const classes = useStyles();
    const learnerDashboard = getRole === USER_ROLES.USER || impersonation.active;
    const superAdminDashboard = getRole === USER_ROLES.SUPER_ADMIN && !impersonation.active;

    const [dashboardCounts, setDashboardCounts] = useState({
        totalCustomer: 0,
        totalProjects: 0,
        totalUser: 0,
        totalClosedProjects: 0,
        totalOpenProjects: 0,
        totalUsedLicenses: 0,
        totalCourses: 0,
        totalAssesment: 0,
        totalLicense: 0,
        totalCourseRequest: 0,
        totalapprenticeship: 0
    });

    const [userDashboardData, setUserDashboardData] = useState({
        courses: 0,
        active_courses: 0,
        completed_courses: 0,
        learning_completion: 0
    });

    const [monthlyVisitorData, setMonthlyVisitorData] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);

    React.useEffect(() => {
        if (!userDetails) {
            return undefined;
        }
        let active = true;
        setPageLoading(true);
        const loadDashboard = async () => {
            const tasks = learnerDashboard
                ? [getUserDashboardDetails()]
                : [getDashboardDetails(), getReportsGraphData(), getTopUsersList()];
            await Promise.allSettled(tasks);
            if (active) {
                setPageLoading(false);
            }
        };
        loadDashboard();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails, impersonation.active, learnerDashboard]);

    const getUserDashboardDetails = async () => {
        const userData = await dashboardService.getDetailDashboardUser();
        if (userData && userData.status && !isEmpty(userData.data)) {
            const { courses, active_courses, completed_courses, learning_completion } = userData.data;
            const completionAvg = learning_completion?.course_completion__avg;

            setUserDashboardData({
                courses: courses || 0,
                active_courses: active_courses || 0,
                completed_courses: completed_courses || 0,
                learning_completion: completionAvg ? Math.round(completionAvg) : 0
            });
        }
    };

    const getDashboardDetails = async () => {
        let userCount = 0;
        if (getRole === USER_ROLES.SUPER_ADMIN) {
            const superAdminDashboard = await dashboardService.getDetailDashboardSuperAdmin();
            if (superAdminDashboard && superAdminDashboard.status && !isEmpty(superAdminDashboard.data)) {
                const { total_users } = superAdminDashboard.data;
                userCount = total_users;
            }
        }
        const dashBoardData = await dashboardService.getDetailDashboardCustomer();
        if (dashBoardData && dashBoardData.status && !isEmpty(dashBoardData.data)) {
            const { customer_count, open_project_count, closed_project_count, course_count, used_license_count, total_license_count, assessment_licenses, course_request_count, license_count, apprenticeship_licenses } = dashBoardData.data;
            let projects = open_project_count + closed_project_count;
            setDashboardCounts({
                totalUser: userCount,
                totalCustomer: customer_count,
                totalProjects: projects,
                totalLicense: total_license_count || license_count,
                totalClosedProjects: closed_project_count,
                totalOpenProjects: open_project_count,
                totalUsedLicenses: used_license_count,
                totalCourses: course_count,
                totalAssesment: assessment_licenses,
                totalCourseRequest: course_request_count,
                totalapprenticeship: apprenticeship_licenses
            });
        }
    };

    const getTopUsersList = async () => {
        const userList = await dashboardService.getTopCustomerAndUsers();
        if (userList?.data && userList?.data.length) {
            setCustomerList(userList.data.map((crs) => {
                return {
                    username: crs.name,
                    email: crs.email,
                    license_count: crs.license_count,
                    date: crs.project_start_date
                };
            }));
        }
    }

    const getReportsGraphData = async () => {
        const graphData = await dashboardService.getPopularCourseGraph();
        if (graphData?.data && graphData?.data.length) {
            setMonthlyVisitorData(graphData.data.map((crs) => {
                return { name: crs.course_name, count: crs.count };
            }));
        }
    };

    const cardData = [
        {
            title: 'Total Customers',
            subTitle: 'Customers',
            image: customerImg,
            totalCount: dashboardCounts.totalCustomer,
        },
        {
            title: 'Total Projects',
            subTitle: 'Projects',
            image: projectsImg,
            totalCount: dashboardCounts.totalProjects,
        },
        {
            title: 'Total Users',
            subTitle: 'Users',
            image: usersImg,
            totalCount: dashboardCounts.totalUser,
        },
        {
            title: 'Total Licenses',
            subTitle: 'Licenses',
            image: compProjImg,
            totalCount: dashboardCounts.totalLicense,
        },
        {
            title: 'Total Completed Projects',
            subTitle: 'Completed Projects',
            image: projectsImg,
            totalCount: dashboardCounts.totalClosedProjects,
        },
        {
            title: 'Total Open Projects',
            subTitle: 'Open Projects',
            image: compProjImg,
            totalCount: dashboardCounts.totalOpenProjects,
        },
        {
            title: 'Total Number of Used Licenses for Customer',
            subTitle: 'Used Licenses for Customer',
            image: usersImg,
            totalCount: dashboardCounts.totalUsedLicenses
        }
    ];

    const customerCardData = [
        {
            title: 'Total Open Projects',
            subTitle: 'Open Projects',
            image: customerImg,
            totalCount: dashboardCounts.totalOpenProjects,
        },
        {
            title: 'Total Closed Projects',
            subTitle: 'Closed Projects',
            image: projectsImg,
            totalCount: dashboardCounts.totalClosedProjects,
        },
        {
            title: 'Total Course Requests',
            subTitle: 'Course Requests',
            image: usersImg,
            totalCount: dashboardCounts.totalCourseRequest,
        },
        {
            title: 'Skills',
            subTitle: 'Skills Transformation',
            image: compProjImg,
            totalCount: '0',
        },
        {
            title: 'Apprenticeships Counts',
            subTitle: 'Total Apprenticeship Counts',
            image: projectsImg,
            totalCount: dashboardCounts?.totalapprenticeship || "0",
        },
        {
            title: 'Assessments Due',
            subTitle: 'Assessments',
            image: usersImg,
            totalCount: dashboardCounts.totalAssesment,
        },
        {
            title: 'License Counts',
            subTitle: 'Total License Counts',
            image: compProjImg,
            totalCount: dashboardCounts.totalLicense,
        }
    ];

    const userCardData = [
        {
            title: 'Total Courses',
            subTitle: 'Courses',
            image: projectsImg,
            totalCount: userDashboardData.courses,
        },
        {
            title: 'Active Courses',
            subTitle: 'Active',
            image: customerImg,
            totalCount: userDashboardData.active_courses,
        },
        {
            title: 'Completed Courses',
            subTitle: 'Completed',
            image: compProjImg,
            totalCount: userDashboardData.completed_courses,
        },
        {
            title: 'Learning Progress',
            subTitle: 'Average Completion',
            image: usersImg,
            totalCount: `${userDashboardData.learning_completion}%`,
        }
    ];

    let cards = superAdminDashboard
        ? cardData
        : learnerDashboard
            ? userCardData
            : customerCardData;

    const pageDescription = superAdminDashboard
        ? 'Overview of customers, projects, licenses, and platform activity.'
        : learnerDashboard
            ? 'Track your courses and learning progress.'
            : 'Overview of projects, licenses, and learning activity for your organisation.';

    return (
        <Layout>
            <main className={classes.page}>
                <div className={classes.pageHeader}>
                    <Typography className={classes.pageEyebrow}>Overview</Typography>
                    <Typography className={classes.pageTitle}>Dashboard</Typography>
                    <Typography className={classes.pageDescription}>{pageDescription}</Typography>
                </div>

                {pageLoading ? <AppLoader minHeight={360} /> : (
                <>
                <Grid container spacing={2}>
                    {cards.map((item) => (
                        <Grid key={item.title} item xs={12} sm={6} md={6} lg={6} xl={4}>
                            <Card className={classes.root} elevation={0}>
                                <LazyCardMedia
                                    className={classes.cover}
                                    image={item.image}
                                    title={item.title}
                                />
                                <CardContent className={classes.content}>
                                    <Typography className={classes.cardTitle}>
                                        {item.title}
                                    </Typography>
                                    <Typography className={classes.cardSubTitle}>
                                        {item.subTitle}
                                    </Typography>
                                    <Typography className={classes.cardValue}>
                                        {item.totalCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {superAdminDashboard ? (
                    <Grid container spacing={2} className={classes.section}>
                        <Grid item xs={12} md={4}>
                            <ChartContainer
                                className={classes.panel}
                                titleClassName={classes.panelTitle}
                                title="Monthly Visitor"
                            >
                                <BarChartWrapper data={monthlyVisitorData} />
                            </ChartContainer>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <ChartContainer
                                className={classes.panel}
                                titleClassName={classes.panelTitle}
                                title="Top Customers/users"
                            >
                                <GenericTable
                                    columns={[
                                        { label: "Username", key: 'username' },
                                        { label: "Learner Email", key: "email" },
                                        { label: "License count", key: "license_count" },
                                        { label: "Date", key: "date" }
                                    ]}
                                    applyMinWidth={false}
                                    list={customerList}
                                />
                            </ChartContainer>
                        </Grid>
                    </Grid>
                ) : null}

                {userDetails && !learnerDashboard ? (
                    <Grid container spacing={2} className={classes.section}>
                        <Grid item xs={12}>
                            <ChartContainer
                                className={classes.panel}
                                titleClassName={classes.panelTitle}
                                title={userDetails.role === USER_ROLES.SUPER_ADMIN ? "Check Customer Growth" : "Check Courses Growth"}
                            >
                                <BarChartWrapper data={monthlyVisitorData} />
                            </ChartContainer>
                        </Grid>
                    </Grid>
                ) : null}
                </>
                )}
            </main>
        </Layout>
    );
}

const DashBoard = () => {
    const impersonation = useImpersonation();
    const isSuperadmin = localStorage.getItem('role') === USER_ROLES.SUPER_ADMIN
        && !impersonation.active;

    if (isSuperadmin) {
        return (
            <Layout>
                <SuperadminInsights />
            </Layout>
        );
    }

    return <LegacyDashboard />;
};

export default DashBoard;
